import { serverTimestamp } from 'firebase/firestore'
import {
  typedDoc,
  typedCollection,
  getDocument,
  getCollection,
  updateDocument,
  orderBy,
} from '@/lib/firebase/firestore'
import { submitNewsletterSignupFn } from '@/lib/firebase/functions'
import type { MailingListMemberDocument, MailingListSegment } from '@/types'
import type { MailingListOptInParams, OptInResult } from '../types'

// Email is the document ID. Direct lookups are restricted by Firestore rules
// to the signed-in member or an admin.
const memberRef = (email: string) =>
  typedDoc<MailingListMemberDocument>('mailingListMembers', email)

const col = () => typedCollection<MailingListMemberDocument>('mailingListMembers')

export const mailingListService = {
  /** Public footer signup. Routes through the rate-limited callable. */
  async subscribe(email: string, firstName: string, lastName: string): Promise<void> {
    await submitNewsletterSignupFn({
      email,
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
      source: 'footer-signup',
    })
  },

  /** Called from service request submission when mailingOptIn === true. */
  async subscribeFromRequest(params: {
    email: string
    firstName: string
    lastName: string
    requestId: string
  }): Promise<void> {
    await submitNewsletterSignupFn({
      email: params.email,
      firstName: params.firstName.trim() || null,
      lastName: params.lastName.trim() || null,
      source: 'service-request',
      sourceRequestId: params.requestId,
    })
  },

  /**
   * General-purpose opt-in used by any form with a mailing list checkbox.
   * Returns 'added' if the contact was created/resubscribed, or
   * 'already-subscribed' if they were already on the list.
   */
  async subscribeOptIn(params: MailingListOptInParams): Promise<OptInResult> {
    const res = await submitNewsletterSignupFn({
      email: params.email,
      firstName: params.firstName?.trim() || null,
      lastName: params.lastName?.trim() || null,
      source: params.source,
      segments: params.segments,
      language: params.language ?? null,
      sourceRequestId: params.sourceRequestId,
    })
    return res.data.result
  },

  /** Look up the signed-in member by email. O(1) — rules enforce ownership. */
  async getMemberByEmail(
    email: string,
  ): Promise<(MailingListMemberDocument & { id: string }) | null> {
    const normalized = email.trim().toLowerCase()
    const ref = memberRef(normalized)
    const doc = await getDocument(ref)
    return doc ? { ...doc, id: normalized } : null
  },

  /** Update which segments a member is subscribed to. Admin-authed. */
  async updateSegments(id: string, segments: MailingListSegment[]): Promise<void> {
    await updateDocument(memberRef(id), {
      segments,
      updatedAt: serverTimestamp(),
    })
  },

  /** Globally unsubscribe a member (preserves segment data). Admin-authed. */
  async unsubscribe(id: string): Promise<void> {
    await updateDocument(memberRef(id), {
      isSubscribed: false,
      unsubscribedAt: new Date(),
      updatedAt: serverTimestamp(),
    })
  },

  /** Re-subscribe a previously unsubscribed member. Admin-authed. */
  async resubscribe(id: string): Promise<void> {
    await updateDocument(memberRef(id), {
      isSubscribed: true,
      unsubscribedAt: null,
      updatedAt: serverTimestamp(),
    })
  },

  /** Fetch all members ordered by join date (admin use only). */
  async getAll(): Promise<Array<MailingListMemberDocument & { id: string }>> {
    return getCollection(col(), orderBy('createdAt', 'desc'))
  },
}
